import { trainStationCodeMap, trainStationCodeMapByWebsiteDomain } from "@/config/trainStationCodeMap"
import getSiteFromDomain from "@/vue/planning/logic/getSiteFromDomain"

/** Eurostar — UK (London St Pancras) */
export const EUROSTAR_TRAIN_ORIGINS_UK = ["STP"] as const

/** Eurostar — NL (ES-suffixed stations) */
export const EUROSTAR_TRAIN_ORIGINS_NL = ["AMSES", "RTMES", "ANRES", "BRUES"] as const

/** All Eurostar origin codes (any site) */
export const EUROSTAR_TRAIN_ORIGINS = [
	...EUROSTAR_TRAIN_ORIGINS_UK,
	...EUROSTAR_TRAIN_ORIGINS_NL,
] as const

/** TNE trains (shared catalogue; NL omits PAZ) */
export const TNE_TRAIN_ORIGINS = ["PAZ", "AMS", "RTM", "ANR", "BRU"] as const
export const TNE_TRAIN_ORIGINS_NL = ["AMS", "RTM", "ANR", "BRU"] as const

export type EurostarTrainOrigin = (typeof EUROSTAR_TRAIN_ORIGINS)[number]
export type TneTrainOrigin = (typeof TNE_TRAIN_ORIGINS)[number]

/**
 * PFPTT + PFPTTUK/PFPTTNL and PFTT + PFTTUK/PFTTNL are the same package type in the UI.
 * Display canonical code depends on site (see getEurostarPackByFamily / getTnePackByFamily).
 */
export const TRAIN_PACKAGE_FAMILIES: Record<string, string[]> = {
	PFPTT: ["PFPTT", "PFPTTF", "PFPTTUK", "PFPTTNL"],
	PFPTTUK: ["PFPTT", "PFPTTF", "PFPTTUK", "PFPTTNL"],
	PFPTTNL: ["PFPTT", "PFPTTF", "PFPTTUK", "PFPTTNL"],
	PFTT: ["PFTT", "PFTTF", "PFTTUK", "PFTTNL"],
	PFTTUK: ["PFTT", "PFTTF", "PFTTUK", "PFTTNL"],
	PFTTNL: ["PFTT", "PFTTF", "PFTTUK", "PFTTNL"],
	HTT: ["HTT", "HTTF", "HTTUK", "HTTNL"],
}

export const isUkWebsite = (): boolean =>
	getSiteFromDomain(window.location.hostname) === "www.uk.travelski.com"

export const isNlWebsite = (): boolean => {
	const site = getSiteFromDomain(window.location.hostname)
	return (
		site === "www.nl.travelski.com" ||
		site === "www.nl.yoonly.eu" ||
		site === "www.benl.yoonly.eu"
	)
}

/**
 * UK: Eurostar = PFPTT/PFTT/HTT, TNE = PFPTTUK/PFTTUK
 * NL: Eurostar = PFPTT/PFTT/HTT (*ES origins), TNE = PFPTTNL/PFTTNL (AMS/RTM/ANR/BRU)
 */
export const getEurostarPackByFamily = (packCode: string): string | undefined => {
	if (isNlWebsite()) {
		const nlEurostar: Record<string, string> = {
			PFPTT: "PFPTT",
			PFPTTNL: "PFPTT",
			PFPTTUK: "PFPTT",
			PFTT: "PFTT",
			PFTTNL: "PFTT",
			PFTTUK: "PFTT",
			HTT: "HTT",
		}
		return nlEurostar[packCode]
	}
	const ukEurostar: Record<string, string> = {
		PFPTT: "PFPTT",
		PFPTTUK: "PFPTT",
		PFPTTNL: "PFPTT",
		PFTT: "PFTT",
		PFTTUK: "PFTT",
		PFTTNL: "PFTT",
		HTT: "HTT",
	}
	return ukEurostar[packCode]
}

export const getTnePackByFamily = (packCode: string): string | undefined => {
	if (isNlWebsite()) {
		const nlTne: Record<string, string> = {
			PFPTT: "PFPTTNL",
			PFPTTNL: "PFPTTNL",
			PFPTTUK: "PFPTTNL",
			PFTT: "PFTTNL",
			PFTTNL: "PFTTNL",
			PFTTUK: "PFTTNL",
		}
		return nlTne[packCode]
	}
	const ukTne: Record<string, string> = {
		PFPTT: "PFPTTUK",
		PFPTTUK: "PFPTTUK",
		PFPTTNL: "PFPTTNL",
		PFTT: "PFTTUK",
		PFTTUK: "PFTTUK",
		PFTTNL: "PFTTNL",
	}
	return ukTne[packCode]
}

export const getEurostarOrigins = (): string[] =>
	isNlWebsite() ? [...EUROSTAR_TRAIN_ORIGINS_NL] : [...EUROSTAR_TRAIN_ORIGINS_UK]

export const getTneOrigins = (): string[] =>
	isNlWebsite() ? [...TNE_TRAIN_ORIGINS_NL] : [...TNE_TRAIN_ORIGINS]

export const isEurostarPackCode = (packCode: string): boolean =>
	getEurostarPackByFamily(packCode) !== undefined

export const isTnePackCode = (packCode: string): boolean =>
	getTnePackByFamily(packCode) !== undefined

/** True when this pack code is the Eurostar product for the current site. */
export const isNativeEurostarPack = (packCode: string): boolean =>
	packCode === getEurostarPackByFamily(packCode)

/** True when this pack code is the TNE product for the current site. */
export const isNativeTnePack = (packCode: string): boolean =>
	packCode === getTnePackByFamily(packCode)

export const isEurostarOrigin = (origin: string): boolean =>
	(EUROSTAR_TRAIN_ORIGINS as readonly string[]).includes(origin)

const TNE_SIBLING_PACKS = ["PFTTUK", "PFPTTUK", "PFTTNL", "PFPTTNL"] as const

/** TNE products (UK/NL night train). They are not Eurostar 7 vs 7+1 stays. */
export const isTneSiblingPackageCode = (code?: string): boolean => {
	if (!code) return false
	return TNE_SIBLING_PACKS.some((pack) => code === pack || code.startsWith(`${pack}_`))
}

/**
 * Eurostar on UK and NL.
 * Skipass 6 is a 7-day Saturday–Saturday stay.
 * Skipass 7 (or a `_8` / HTT_PLUS1 code) is 7 nights in resort + 1 night near the station.
 */
export const isEurostarSevenPlusOne = (offer: {
	codePackage?: string
	skipass?: number | string
}): boolean => {
	const code = offer.codePackage || ""
	if (isTneSiblingPackageCode(code)) return false
	if (code.includes("_8") || code.includes("PLUS1")) return true
	if (!(isUkWebsite() || isNlWebsite())) return false
	return Number(offer.skipass) === 7
}

/** Pack-options code used to render inclusions. HTT skipass 7 includes the extra hotel night. */
export const resolveDisplayedTrainPackCode = (
	codePackage?: string,
	skipass?: number | string
): string => {
	if (
		codePackage === "HTT" &&
		isEurostarSevenPlusOne({ codePackage, skipass })
	) {
		return "HTT_PLUS1"
	}
	return codePackage || ""
}

export const isTneOrigin = (origin: string): boolean =>
	(TNE_TRAIN_ORIGINS as readonly string[]).includes(origin)

export const isTrainStationOrigin = (origin?: string | null): boolean => {
	const originCode = origin?.split("-")[0] || ""
	return isEurostarOrigin(originCode) || isTneOrigin(originCode)
}

export const getTrainPackageFamily = (packCode: string): string[] =>
	TRAIN_PACKAGE_FAMILIES[packCode] ?? [packCode]

export const getCanonicalTrainPackCode = (packCode: string): string =>
	getEurostarPackByFamily(packCode) ?? packCode

/** TNE packs fall back to Eurostar when WIYP has no prices for the requested code. */
export const getEurostarFallbackPack = (packCode: string): string | undefined => {
	if (isNlWebsite()) {
		if (packCode === "PFTTNL") return "PFTT"
		if (packCode === "PFPTTNL") return "PFPTT"
		return undefined
	}
	if (packCode === "PFTTUK") return "PFTT"
	if (packCode === "PFPTTUK") return "PFPTT"
	return undefined
}

export const isSameTrainPackageType = (packCodeA: string, packCodeB: string): boolean => {
	const family = TRAIN_PACKAGE_FAMILIES[packCodeA]
	if (!family) return packCodeA === packCodeB
	return family.includes(packCodeB)
}

/** Origins allowed for a train pack type. `null` means no extra restriction. */
export const getAllowedOriginsForTrainPack = (packCode: string): string[] | null => {
	// HTT is Eurostar-only (UK: STP, NL: *ES stations)
	if (packCode === "HTT" || packCode === "HTTUK" || packCode === "HTTNL") {
		return getEurostarOrigins()
	}
	if ((TRAIN_PACKAGE_FAMILIES[packCode]?.length ?? 0) > 1) {
		return [...getEurostarOrigins(), ...getTneOrigins()]
	}
	return null
}

/** Origins that belong to this pack code itself (Eurostar vs TNE), not the whole family. */
export const getNativeOriginsForTrainPack = (packCode: string): string[] | null => {
	if (!TRAIN_PACKAGE_FAMILIES[packCode] && packCode !== "HTT") return null
	if (isNativeEurostarPack(packCode)) {
		return getEurostarOrigins()
	}
	if (isNativeTnePack(packCode)) {
		return getTneOrigins()
	}
	return null
}

export const resolveTrainPackCodeForOrigin = (
	packCode: string,
	origin?: string | null
): string => {
	const family = TRAIN_PACKAGE_FAMILIES[packCode]
	if (!family) return packCode
	if (family.length === 1) return family[0]

	const originCode = origin?.split("-")[0] || ""
	if (isTneOrigin(originCode)) {
		return getTnePackByFamily(packCode) ?? packCode
	}
	if (isEurostarOrigin(originCode)) {
		return getEurostarPackByFamily(packCode) ?? packCode
	}
	return getCanonicalTrainPackCode(packCode)
}

export const variantBelongsToPackCode = (
	variantCode: string | undefined,
	packCode: string
): boolean => {
	if (!variantCode) return false
	return variantCode === packCode || variantCode.startsWith(`${packCode}_`)
}

export const dedupeTrainPackagesForDisplay = <T extends { codePackage: string }>(
	packs: T[]
): T[] => {
	const seenFamilies = new Set<string>()
	const result: T[] = []

	for (const pack of packs) {
		const family = TRAIN_PACKAGE_FAMILIES[pack.codePackage]
		if (!family) {
			result.push(pack)
			continue
		}
		const familyKey = family[0]
		if (seenFamilies.has(familyKey)) continue

		const preferredCode = getCanonicalTrainPackCode(pack.codePackage)
		const preferredPack = packs.find((p) => p.codePackage === preferredCode)
		const canonicalPack = packs.find((p) => p.codePackage === familyKey)
		result.push(preferredPack ?? canonicalPack ?? pack)
		seenFamilies.add(familyKey)
	}

	return result
}

const toOriginCode = (origin: string): string => origin.split("-")[0] || origin

const trainStationsFrom = (origins: string[]): string[] =>
	origins.map(toOriginCode).filter(isTrainStationOrigin)

const byStationName = (a: string, b: string): number => {
	const nameA = trainStationCodeMap.get(a)?.name || a
	const nameB = trainStationCodeMap.get(b)?.name || b
	return nameA.localeCompare(nameB, undefined, { sensitivity: "base" }) || a.localeCompare(b)
}

const firstStationAlphabetically = (origins: string[]): string | undefined =>
	[...origins].sort(byStationName)[0]

export const getPreferredTrainOrigin = (
	packCode: string,
	availableOrigins: string[] = []
): string | undefined => {
	const native = getNativeOriginsForTrainPack(packCode)
	const allowed = native ?? getAllowedOriginsForTrainPack(packCode)
	const country = getSiteFromDomain(window.location.hostname)
	const domainStations = Array.from(
		(trainStationCodeMapByWebsiteDomain[country] ?? new Map()).keys()
	)
	const trainAvailable = trainStationsFrom(availableOrigins)
		.filter((origin) => !allowed || allowed.includes(origin))

	// A non-empty list that contains no station for this pack came from
	// availability (or a flight search). Do not invent a static default such as AMSES.
	if (availableOrigins.length > 0 && trainAvailable.length === 0) {
		return undefined
	}

	const pool = (trainAvailable.length ? trainAvailable : allowed ?? domainStations)
		.filter((origin) => !allowed || allowed.includes(origin))

	return firstStationAlphabetically(pool) ?? allowed?.[0]
}

/** Origins the availability endpoint actually returned for this train pack. */
export const collectAvailabilityOriginsForTrainPack = (
	items: Array<{ codePackage?: string; origins?: string[] }>,
	packCode: string
): string[] => {
	const family = getTrainPackageFamily(packCode)
	const native = getNativeOriginsForTrainPack(packCode)
	const allowed = native ?? getAllowedOriginsForTrainPack(packCode)
	const origins = new Set<string>()

	for (const item of items) {
		const code = item.codePackage
		if (!code || !family.some((familyPack) => code === familyPack || variantBelongsToPackCode(code, familyPack))) {
			continue
		}
		if (!Array.isArray(item.origins)) continue
		for (const origin of item.origins) {
			const originCode = toOriginCode(origin)
			if (!originCode) continue
			if (allowed && !allowed.includes(originCode)) continue
			origins.add(originCode)
		}
	}

	return [...origins]
}

export const resolveTrainOriginForPack = (
	packCode: string,
	currentOrigin: string | null | undefined,
	availableOrigins: string[] = [],
	options?: { preferNative?: boolean }
): string | undefined => {
	const native = getNativeOriginsForTrainPack(packCode)
	const allowed = getAllowedOriginsForTrainPack(packCode)
	const originCode = currentOrigin?.split("-")[0] || ""
	const preferNative = options?.preferNative === true
	const preferred = preferNative ? (native ?? allowed) : allowed

	// Keep a station already chosen (URL / user) if it is valid for this pack type.
	// With preferNative, reject TNE origins on Eurostar packs (and vice versa) —
	// critical on NL where AMS/BRU are both flight airports and TNE stations.
	if (
		isTrainStationOrigin(originCode) &&
		(!preferred || preferred.includes(originCode))
	) {
		return originCode
	}

	if (preferNative) {
		return getPreferredTrainOrigin(packCode, availableOrigins)
	}

	const trainAvailable = trainStationsFrom(availableOrigins)
	const pool = (trainAvailable.length ? trainAvailable : allowed ?? [])
		.filter((origin) => !allowed || allowed.includes(origin))

	return firstStationAlphabetically(pool) ?? getPreferredTrainOrigin(packCode, trainAvailable)
}

export const prestationMatchesOrigin = (
	prestation: { origins?: string[] },
	origin?: string | null
): boolean => {
	const originCode = origin?.split("-")[0] || ""
	if (!originCode) return true
	if (!Array.isArray(prestation.origins) || prestation.origins.length === 0) {
		return true
	}
	return prestation.origins.includes(originCode)
}

export const findTrainPrestIdForOrigin = (
	items: Array<{ codePackage?: string; prestId?: string; origins?: string[] }>,
	packCode: string,
	origin?: string | null
): string | undefined => {
	const family = getTrainPackageFamily(packCode)
	const belongsToFamily = (codePackage?: string) =>
		!!codePackage && family.some((familyPack) => variantBelongsToPackCode(codePackage, familyPack))

	const originMatch = items.find((item) => {
		if (!item.prestId || !belongsToFamily(item.codePackage)) {
			return false
		}
		return prestationMatchesOrigin(item, origin)
	})
	if (originMatch?.prestId) return originMatch.prestId

	const resolvedPack = resolveTrainPackCodeForOrigin(packCode, origin)
	const packMatch = items.find((item) => item.prestId && variantBelongsToPackCode(item.codePackage, resolvedPack))
	return packMatch?.prestId
}
